import { useCallback, useMemo, useState } from 'react'

import type { MessageInitShape } from '@bufbuild/protobuf'
import { create } from '@bufbuild/protobuf'
import { type ConnectError } from '@connectrpc/connect'
import { createConnectQueryKey, useTransport } from '@connectrpc/connect-query'
import {
  type ImputationMethod,
  type ImputationPolicy,
  ImputationPolicySchema,
} from '@ebalance/proto/bffuser/v1/imputation_policy_model_pb'
import { getImputationPolicy } from '@ebalance/proto/bffuser/v1/imputation_policy_service-ImputationPolicyService_connectquery'
import {
  GetImputationPolicyRequestSchema,
  type GetImputationPolicyResponse,
  GetImputationPolicyResponseSchema,
  UpdateImputationPolicyRequestSchema,
} from '@ebalance/proto/bffuser/v1/imputation_policy_service_pb'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import {
  DEFAULT_DEMAND_METHOD,
  DEFAULT_GENERATION_METHOD_ORDER,
  FIELD_PATHS,
} from '~/pages/BalanceImputationPolicy/constants/defaults'
import { useGetImputationPolicyQuery } from '~/pages/BalanceImputationPolicy/hooks/useGetImputationPolicyQuery'
import { useUpdateImputationPolicyMutation } from '~/pages/BalanceImputationPolicy/hooks/useUpdateImputationPolicyMutation'

/** 補完方式を設定する対象 (= 画面のタブ)。 */
export type ImputationTarget = 'demand' | 'generation'

/** 何の保存か (保存成功 toast の文言を決める)。 */
type SaveKind = {
  target: ImputationTarget
  /** update = 方式の選択 / 順番の入替、 reset = リセット (既定値への上書き)。 */
  action: 'update' | 'reset'
}

/**
 * 保存成功 toast の文言。 「保存しました」 だけだと何が保存されたかが読めない
 * ので、 保存した設定を主語にする (design の保存表示 `persist(msg)` と同じ文言)。
 */
const SAVED_MESSAGE_KEY = {
  demand: {
    update: 'page:balanceImputationPolicy.saved.demand',
    reset: 'page:balanceImputationPolicy.saved.demandReset',
  },
  generation: {
    update: 'page:balanceImputationPolicy.saved.generation',
    reset: 'page:balanceImputationPolicy.saved.generationReset',
  },
} as const satisfies Record<
  ImputationTarget,
  Record<SaveKind['action'], string>
>

/** 保存成功 toast の表示時間 (ms)。 個人設定の保存 toast (`useSaveSuccessToast`) と同値。 */
const SAVE_TOAST_DURATION_MS = 2500

/** proto Message プロパティを除いた純粋なフィールド値の部分型 */
type ImputationPolicyFields = MessageInitShape<typeof ImputationPolicySchema>

type ImputationPolicyPartial = Partial<ImputationPolicyFields>

/**
 * 現在の設定値と部分上書き値をマージして MessageInit 型を返す。
 * proto Message 型を直接スプレッドすると $typeName 等の内部プロパティが
 * MessageInit に混入して型エラーになるため、フィールドを明示的に取り出す。
 *
 * ImputationPolicy は strict protovalidate rules (enum.not_in: [0],
 * generation_method_order.min_items = 2) のため、全フィールドを populate して
 * 送る必要がある。ただし backend が書くのは update_mask に載せた field だけで、
 * 載せなかった field の値は無視される (= 古い値で他ユーザーの編集を上書きしない)。
 * TODO(#556): true partial payload への移行。
 */
function mergePolicy(
  current: ImputationPolicy | undefined,
  partial: ImputationPolicyPartial,
): ImputationPolicyFields {
  const currentOrder =
    current?.generationMethodOrder != null &&
    current.generationMethodOrder.length > 0
      ? current.generationMethodOrder
      : DEFAULT_GENERATION_METHOD_ORDER

  return {
    demandMethod:
      partial.demandMethod ?? current?.demandMethod ?? DEFAULT_DEMAND_METHOD,
    generationMethodOrder: partial.generationMethodOrder ?? [...currentOrder],
    // updatedAt は response 専用 (Update では backend が無視する)。
    updatedAt: current?.updatedAt,
  }
}

type UseImputationPolicyReturn = {
  policy: ImputationPolicy | undefined
  isLoading: boolean
  /** 保存中 (= 直列化のため画面側で操作を止める)。 */
  isSaving: boolean
  error: ConnectError | null
  /**
   * 保存失敗のたびに増える再同期トークン。
   *
   * ラジオは DS の controlled component だが、click で DS 内部状態が動いた後に
   * rollback すると `value` prop は「クリック前の値」= 変化なしに戻るため、
   * prop 変化を検知できず DOM の選択がユーザーのクリックのまま残る。
   * このトークンを component の key に混ぜて再マウントさせ、spec 異常系
   * 「前の設定を維持」 を成立させる。
   */
  resetToken: number
  updateDemandMethod: (value: ImputationMethod) => void
  moveGenerationMethodUp: (index: number) => void
  moveGenerationMethodDown: (index: number) => void
  /** 指定したタブの設定だけを spec の既定値に戻す。 */
  resetToDefault: (target: ImputationTarget) => void
  /**
   * タブごとに、 表示中の設定が既に spec の既定値と一致しているか
   * (= `resetToDefault(target)` を呼んでも何も変わらない状態か)。
   *
   * 「何が既定値か」 の知識は `resetToDefault` が既に持っているので、 判定も
   * ここに置く (画面側で既定値定数を読み直すと、 既定値を変えた時に
   * 送信内容と活性判定が別々に育って食い違う)。
   */
  isAlreadyDefault: Record<ImputationTarget, boolean>
}

/**
 * 欠損補完設定の Get + Update を束ねる統合フック。
 *
 * 自動保存 (spec AC-003 / AC-009):
 * - 需要のラジオ変更は即時 mutation
 * - 発電の ▲▼ も押下 (= drop 確定相当) ごとに即時 mutation。
 *   design/balance/imputation-policy.html はボタン方式なので「ドラッグ中の
 *   途中状態」 が構造的に存在せず、 AC-009 NOTE は自動的に満たされる。
 *
 * optimistic update パターン (usePersonalSettings と同型):
 * - snapshot 取得 → queryClient に楽観値を set
 * - onSuccess: 保存成功 toast (即時保存で保存ボタンが無いため、 これが無いと
 *   「保存できたのか」 が画面から読めない。 2026-10-05 デザイナー判断で DS toast)
 * - onError: snapshot へ rollback + 保存失敗 toast (操作の結果は toast で伝え、 画面に
 *   Alert を重ねない。 UI 辞書 components/alert-placement.md「置き場所が決まっているもの」)
 * - onSettled: query を invalidate
 */
export function useImputationPolicy(): UseImputationPolicyReturn {
  const { t } = useTranslation()
  const successToast = useSuccessToast()
  const errorToast = useErrorToast()
  const queryClient = useQueryClient()
  const { data, isLoading, error } = useGetImputationPolicyQuery()
  const mutation = useUpdateImputationPolicyMutation()
  const [resetToken, setResetToken] = useState(0)

  // query key (optimistic update / invalidate に使う)。
  //
  // input / transport を必ず含める: connect-query の useQuery は
  // createQueryOptions 経由で {schema, input, transport, cardinality:'finite'}
  // からキーを組む。ここで input / transport を省くと **別キー** になり、
  // setQueryData (= 完全一致マッチ) が幻のエントリに書き込まれて optimistic
  // 更新も rollback も画面に反映されない (invalidateQueries は前方一致なので
  // refetch だけは動き、不具合が見えにくい)。
  //
  // useMemo で pin しないと毎レンダー再生成され、後段の useCallback chain が
  // 全壊する。
  const transport = useTransport()
  const queryKey = useMemo(
    () =>
      createConnectQueryKey({
        schema: getImputationPolicy,
        input: create(GetImputationPolicyRequestSchema, {}),
        transport,
        cardinality: 'finite',
      }),
    [transport],
  )

  const applyOptimistic = useCallback(
    (partial: ImputationPolicyPartial) => {
      queryClient.setQueryData<GetImputationPolicyResponse | undefined>(
        queryKey,
        (old) => {
          const merged = create(
            ImputationPolicySchema,
            mergePolicy(old?.policy, partial),
          )
          return create(GetImputationPolicyResponseSchema, { policy: merged })
        },
      )
    },
    [queryClient, queryKey],
  )

  const mutate = useCallback(
    (partial: ImputationPolicyPartial, paths: string[], kind: SaveKind) => {
      // 初期取得が終わる前は保存しない。mergePolicy が未編集 field を既定値で
      // 埋めるため、取得前に送ると payload が実際の保存値と乖離する。backend は
      // mask 外の field を書かないので設定は消えないが、編集した field の
      // 楽観更新が誤った現在値を土台にする (画面側でも取得前は操作させないが、
      // 二重の防御)。
      if (data?.policy == null) {
        return
      }
      // 保存は直列化する。並行させると (a) 到着順が操作順と逆転した時に
      // last-writer-wins で古い操作が残り、(b) 先行 mutation の失敗時の rollback
      // が後続の楽観値を巻き戻す。各 payload が全 field を運ぶため、どちらも
      // 「触っていない設定が戻る」 形で表面化する。画面側でも保存中は操作させない
      // (二重の防御)。
      if (mutation.isPending) {
        return
      }

      // snapshot (rollback 用)
      const previous = queryClient.getQueryData<
        GetImputationPolicyResponse | undefined
      >(queryKey)

      applyOptimistic(partial)

      mutation.mutate(
        create(UpdateImputationPolicyRequestSchema, {
          policy: create(
            ImputationPolicySchema,
            mergePolicy(data.policy, partial),
          ),
          updateMask: { paths },
        }),
        {
          // 保存の成功表示は楽観更新の時点でなく応答後に出す (失敗して戻る
          // 設定に「保存しました」 を出さない)。 mutate() に渡した callback は
          // 画面を離れた後には呼ばれないので、 別画面に toast は漏れない。
          onSuccess: () => {
            successToast({
              title: t(SAVED_MESSAGE_KEY[kind.target][kind.action]),
              duration: SAVE_TOAST_DURATION_MS,
            })
          },
          onError: () => {
            queryClient.setQueryData(queryKey, previous)
            errorToast({ title: t('page:balanceImputationPolicy.saveError') })
            setResetToken((token) => token + 1)
          },
          onSettled: () => {
            void queryClient.invalidateQueries({ queryKey })
          },
        },
      )
    },
    [
      queryClient,
      queryKey,
      mutation,
      data,
      applyOptimistic,
      successToast,
      errorToast,
      t,
    ],
  )

  const updateDemandMethod = useCallback(
    (value: ImputationMethod) => {
      mutate({ demandMethod: value }, [FIELD_PATHS.demandMethod], {
        target: 'demand',
        action: 'update',
      })
    },
    [mutate],
  )

  /** index と index-1 を入れ替えた新配列で保存する (先頭では何もしない)。 */
  const moveGenerationMethodUp = useCallback(
    (index: number) => {
      const current =
        data?.policy?.generationMethodOrder ?? DEFAULT_GENERATION_METHOD_ORDER
      if (index <= 0 || index >= current.length) {
        return
      }
      const next = [...current]
      const tmp = next[index - 1]
      next[index - 1] = next[index]
      next[index] = tmp
      mutate(
        { generationMethodOrder: next },
        [FIELD_PATHS.generationMethodOrder],
        { target: 'generation', action: 'update' },
      )
    },
    [data, mutate],
  )

  /** index と index+1 を入れ替えた新配列で保存する (末尾では何もしない)。 */
  const moveGenerationMethodDown = useCallback(
    (index: number) => {
      const current =
        data?.policy?.generationMethodOrder ?? DEFAULT_GENERATION_METHOD_ORDER
      if (index < 0 || index >= current.length - 1) {
        return
      }
      const next = [...current]
      const tmp = next[index + 1]
      next[index + 1] = next[index]
      next[index] = tmp
      mutate(
        { generationMethodOrder: next },
        [FIELD_PATHS.generationMethodOrder],
        { target: 'generation', action: 'update' },
      )
    },
    [data, mutate],
  )

  /**
   * 表示中のタブの設定だけを spec の既定値に戻す (design の「リセット」)。
   *
   * [mock差異] 竹モックは需要・発電をまとめて戻すが、 design は「片方のタブしか
   * 見ていないのに両方が変わるのは違和感がある」 というデザイナー判断
   * (2026-07-28) でタブ単位にしている。 見えている範囲だけが変わる方が
   * 結果を予測できるため design に揃える。
   */
  const resetToDefault = useCallback(
    (target: ImputationTarget) => {
      if (target === 'demand') {
        mutate(
          { demandMethod: DEFAULT_DEMAND_METHOD },
          [FIELD_PATHS.demandMethod],
          { target, action: 'reset' },
        )
        return
      }
      mutate(
        { generationMethodOrder: [...DEFAULT_GENERATION_METHOD_ORDER] },
        [FIELD_PATHS.generationMethodOrder],
        { target, action: 'reset' },
      )
    },
    [mutate],
  )

  // `resetToDefault` が送る値と同じ定数で判定する (上記 JSDoc 参照)。
  // 未取得時は画面側と同じ既定値フォールバックを使う (= 初回表示から
  // 「既に既定値」 と判定でき、 取得完了後にボタンの活性が跳ねない)。
  const currentDemandMethod =
    data?.policy?.demandMethod ?? DEFAULT_DEMAND_METHOD
  const currentOrderForCompare =
    data?.policy?.generationMethodOrder != null &&
    data.policy.generationMethodOrder.length > 0
      ? data.policy.generationMethodOrder
      : DEFAULT_GENERATION_METHOD_ORDER
  const isAlreadyDefault: Record<ImputationTarget, boolean> = {
    demand: currentDemandMethod === DEFAULT_DEMAND_METHOD,
    generation:
      currentOrderForCompare.length ===
        DEFAULT_GENERATION_METHOD_ORDER.length &&
      currentOrderForCompare.every(
        (m, i) => m === DEFAULT_GENERATION_METHOD_ORDER[i],
      ),
  }

  return {
    policy: data?.policy,
    isLoading,
    isSaving: mutation.isPending,
    error,
    resetToken,
    updateDemandMethod,
    moveGenerationMethodUp,
    moveGenerationMethodDown,
    resetToDefault,
    isAlreadyDefault,
  }
}
